// frontend/src/lib/utils.ts
// Utilidades para shadcn/ui

import { type ClassValue, clsx } from "clsx"
import { twMerge } from "tailwind-merge"
import type { Cadete } from "./api"

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

// Hora local de Argentina (America/Argentina/Buenos_Aires) en partes.
export function getArgentinaParts(date: Date = new Date()) {
  const formatter = new Intl.DateTimeFormat('en-US', {
    timeZone: 'America/Argentina/Buenos_Aires',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  });
  const parts = formatter.formatToParts(date);
  const partMap = Object.fromEntries(parts.map(p => [p.type, p.value]));
  
  // handle potentially different formatting / missing parts
  const year = partMap.year || date.getFullYear().toString();
  const month = partMap.month || (date.getMonth() + 1).toString().padStart(2, '0');
  const day = partMap.day || date.getDate().toString().padStart(2, '0');
  let hour = partMap.hour || date.getHours().toString().padStart(2, '0');
  const minute = partMap.minute || date.getMinutes().toString().padStart(2, '0');
  const second = partMap.second || date.getSeconds().toString().padStart(2, '0');

  if (hour === '24') hour = '00';
  return { year, month, day, hour, minute, second };
}

// Devuelve 'YYYY-MM-DD' en la zona horaria de Argentina.
export function getArgentinaDateString(date: Date = new Date()): string {
  const { year, month, day } = getArgentinaParts(date);
  return `${year}-${month}-${day}`;
}

// Devuelve un objeto Date cuyas funciones locales (.getHours(), .getDate(), etc.) 
// devuelven los valores correspondientes a la zona horaria de Argentina.
export function getArgentinaNow(): Date {
  const { year, month, day, hour, minute, second } = getArgentinaParts(new Date());
  return new Date(
    parseInt(year),
    parseInt(month) - 1,
    parseInt(day),
    parseInt(hour),
    parseInt(minute),
    parseInt(second)
  );
}

// Hora local en formato HH:MM:SS, en la zona horaria de Argentina.
// Evita toLocaleTimeString, cuyo formato varía según el entorno/locale.
export function formatHoraLocal(date: Date = new Date()): string {
  const { hour, minute, second } = getArgentinaParts(date);
  return `${hour}:${minute}:${second}`;
}

// Devuelve 'YYYY-MM-DD' a partir de un objeto Date local, evitando corrimientos por UTC.
export function toLocalDateString(d: Date): string {
  const yyyy = d.getFullYear();
  const mm = (d.getMonth() + 1).toString().padStart(2, '0');
  const dd = d.getDate().toString().padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
}



// Estados institucionales que sacan al cadete de la fuerza efectiva (Traspaso además de la convocada)
export const ESTADOS_QUE_ALTERAN_FUERZA: string[] = ['Desestimiento', 'Excluido', 'Ausente a Convocatoria', 'Traspaso'];

// Estado institucional que tenía el cadete el día `fecha` ('YYYY-MM-DD'). La baja rige
// desde estado_metadata.fecha (inclusive): antes de esa fecha todavía era 'Cadete'.
// Sin fecha de baja, el estado actual rige siempre. Misma regla que el backend
// (backend/src/utils/estadoInstitucional.js).
export function estadoInstitucionalEnFecha(
  c: Pick<Cadete, 'estado_institucional' | 'estado_metadata'>,
  fecha: string,
): Cadete['estado_institucional'] {
  const estado = c.estado_institucional;
  if (!ESTADOS_QUE_ALTERAN_FUERZA.includes(estado || '')) return estado;
  const desde = /^(\d{4}-\d{2}-\d{2})/.exec(String(c.estado_metadata?.fecha ?? '').trim())?.[1];
  return desde && fecha < desde ? 'Cadete' : estado;
}

// true si el cadete formaba parte de la fuerza efectiva el día `fecha`.
export function esEfectivoEnFecha(c: Pick<Cadete, 'estado_institucional' | 'estado_metadata'>, fecha: string): boolean {
  return !ESTADOS_QUE_ALTERAN_FUERZA.includes(estadoInstitucionalEnFecha(c, fecha) || '');
}
